import { useCallback, useEffect, useRef, useState } from "react";
import {
  listMemoryReview,
  decideMemoryReview,
  type MemoryReviewItem,
  type MemoryReviewPage,
} from "@/api/memory";
import {
  captureDesktopChatAuthScopeGuard,
  subscribeDesktopChatAuthScope,
} from "@/desktop/desktop-chat-auth-scope";
import { translate } from "@/i18n";
import { settingsMemoryMessages } from "@/i18n/messages/settings-memory";
import { useMemoryStore } from "@/stores/memory-store";

const CHANGED = "denker-memory-review-changed";
export function useMemoryReviewCount(enabled = true) {
  const [total, setTotal] = useState<number | null>(null);
  useEffect(() => {
    let active = true;
    let generation = 0;
    const refresh = () => {
      const request = ++generation;
      if (!enabled) {
        setTotal(0);
        return;
      }
      const scope = captureDesktopChatAuthScopeGuard();
      void listMemoryReview({ state: "pending", limit: 1 })
        .then((page) => {
          if (active && request === generation && scope())
            setTotal(page.total ?? null);
        })
        .catch(() => {
          if (active && request === generation && scope()) setTotal(null);
        });
    };
    refresh();
    window.addEventListener(CHANGED, refresh);
    const unsubscribe = subscribeDesktopChatAuthScope(() => {
      setTotal(null);
      refresh();
    });
    return () => {
      active = false;
      unsubscribe();
      window.removeEventListener(CHANGED, refresh);
    };
  }, [enabled]);
  return total;
}

export function useMemoryReview(enabled = true) {
  const [pending, setPending] = useState<MemoryReviewPage | null>(null);
  const [recent, setRecent] = useState<MemoryReviewItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const generation = useRef(0);
  const invalidate = useCallback(() => {
    generation.current++;
  }, []);
  const reload = useCallback(async () => {
    const request = ++generation.current;
    if (!enabled) {
      setPending({
        items: [],
        total: 0,
        pagination: { has_more: false, next_cursor: null },
      });
      setRecent([]);
      setError(null);
      return;
    }
    const scope = captureDesktopChatAuthScopeGuard();
    try {
      const [queue, decisions] = await Promise.all([
        listMemoryReview({ state: "pending" }),
        listMemoryReview({ state: "recent", limit: 5 }),
      ]);
      if (request !== generation.current || !scope()) return;
      setPending(queue);
      setRecent(decisions.items);
      setError(null);
    } catch {
      if (request === generation.current && scope())
        setError(translate(settingsMemoryMessages, "loadError"));
    }
  }, [enabled]);
  useEffect(() => {
    void reload();
    const unsubscribe = subscribeDesktopChatAuthScope(() => {
      setPending(null);
      setRecent([]);
      setBusy(false);
      void reload();
    });
    return () => {
      unsubscribe();
      invalidate();
    };
  }, [reload, invalidate]);

  async function loadMore() {
    const cursor = pending?.pagination.next_cursor;
    if (!cursor || busy) return;
    const scope = captureDesktopChatAuthScopeGuard();
    const request = generation.current;
    setBusy(true);
    try {
      const page = await listMemoryReview({ state: "pending", cursor });
      if (request !== generation.current || !scope()) return;
      setPending((current) => ({
        ...page,
        items: [
          ...new Map(
            [...(current?.items ?? []), ...page.items].map((item) => [
              item.id,
              item,
            ]),
          ).values(),
        ],
      }));
    } catch {
      if (scope()) setError(translate(settingsMemoryMessages, "loadMoreError"));
    } finally {
      if (scope()) setBusy(false);
    }
  }

  async function decide(
    items: MemoryReviewItem[],
    decision: "accept" | "reject" | "undo",
    all = false,
  ) {
    if (busy) return false;
    setBusy(true);
    setError(null);
    const scope = captureDesktopChatAuthScopeGuard();
    const request = generation.current;
    let failures = 0;
    try {
      const queue = [...items];
      let cursor = all ? pending?.pagination.next_cursor : null;
      while (cursor) {
        const page = await listMemoryReview({ state: "pending", cursor });
        if (!scope() || request !== generation.current) return false;
        queue.push(...page.items);
        cursor = page.pagination.next_cursor;
      }
      // Serial decisions avoid flooding the API and let us retain individual failures.
      for (const item of new Map(
        queue.map((item) => [item.id, item]),
      ).values()) {
        if (!scope() || request !== generation.current) return false;
        try {
          await decideMemoryReview(item.id, decision);
        } catch {
          failures++;
        }
      }
      if (!scope() || request !== generation.current) return false;
      await reload();
      void useMemoryStore.getState().refresh();
      window.dispatchEvent(new Event(CHANGED));
      if (failures)
        setError(
          translate(settingsMemoryMessages, "saveFailures", {
            count: failures,
          }),
        );
      return failures === 0;
    } catch {
      if (scope())
        setError(translate(settingsMemoryMessages, "saveChangesError"));
      return false;
    } finally {
      if (scope()) setBusy(false);
    }
  }
  return { pending, recent, error, busy, reload, loadMore, decide };
}
